import React, { useEffect, useState } from 'react';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';
import ApprovalChainsConfig from '../components/ApprovalChainsConfig';
import ChatDepartmentsConfig from '../components/ChatDepartmentsConfig';
import FmcsaCarrierPanel from '../components/FmcsaCarrierPanel';
import CompanyExportCard from '../components/CompanyExportCard';
import { Settings as SettingsIcon, BarChart3, TrendingUp, Briefcase, Save, CheckCircle, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURE_META = {
  analytics: { label: 'Analytics Dashboard', icon: BarChart3, blurb: 'Overall KPIs, loads timeline, top drivers, payroll trends.' },
  truck_pnl: { label: 'Truck P&L', icon: TrendingUp, blurb: 'Per-truck revenue, fuel, maintenance, profit per mile.' },
  brokers: { label: 'Broker Performance', icon: Briefcase, blurb: 'Revenue & rates aggregated by broker.' },
};

const FEATURES = ['analytics', 'truck_pnl', 'brokers'];
const ROLES = ['dispatcher', 'driver'];

const ROLE_LABEL = { dispatcher: 'Dispatchers', driver: 'Drivers' };

const AdminSettings = () => {
  const [perms, setPerms] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/company/settings`, { withCredentials: true });
        setPerms(res.data.analytics_permissions);
      } catch (e) {
        setToast({ kind: 'error', msg: 'Failed to load settings' });
      } finally { setLoading(false); }
    })();
  }, []);

  const toggle = (role, feature) => {
    setPerms(prev => ({
      ...prev,
      [role]: { ...prev[role], [feature]: !prev[role][feature] },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/company/settings/analytics-permissions`, { permissions: perms }, { withCredentials: true });
      setPerms(res.data.analytics_permissions);
      setToast({ kind: 'success', msg: 'Settings saved' });
    } catch (e) {
      setToast({ kind: 'error', msg: e.response?.data?.detail || 'Failed to save' });
    } finally {
      setSaving(false);
      setTimeout(() => setToast(null), 3500);
    }
  };

  if (loading || !perms) return <DashboardLayout><div className="p-8 text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Loading settings...</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-4xl mx-auto" data-testid="admin-settings-page">
        <div className="mb-8 flex items-center gap-3">
          <div className="w-10 h-10 bg-[#002FA7]/10 rounded-md flex items-center justify-center">
            <SettingsIcon className="w-5 h-5 text-[#002FA7]" />
          </div>
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Company Settings</h1>
            <p className="text-base text-slate-600 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Control what your dispatchers and drivers can see.</p>
          </div>
        </div>

        {toast && (
          <div
            data-testid="settings-toast"
            className={`mb-4 px-4 py-3 rounded-md text-sm flex items-center gap-2 border ${toast.kind === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            {toast.kind === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
            {toast.msg}
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-md p-6 mb-6">
          <h2 className="text-lg font-bold tracking-tight text-slate-900 mb-1" style={{ fontFamily: 'Work Sans, sans-serif' }}>Analytics Visibility</h2>
          <p className="text-xs text-slate-600 mb-6" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            Toggle access per role. Company admins always have full access.
          </p>

          <div className="space-y-5">
            {FEATURES.map(feat => {
              const Icon = FEATURE_META[feat].icon;
              return (
                <div key={feat} className="border-t border-slate-100 pt-5 first:border-t-0 first:pt-0">
                  <div className="flex items-start gap-3 mb-3">
                    <Icon className="w-5 h-5 text-[#002FA7] flex-shrink-0 mt-0.5" strokeWidth={2} />
                    <div>
                      <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{FEATURE_META[feat].label}</h3>
                      <p className="text-xs text-slate-600 mt-0.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{FEATURE_META[feat].blurb}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 ml-8">
                    {ROLES.map(role => (
                      <Toggle
                        key={role}
                        testid={`toggle-${role}-${feat}`}
                        label={ROLE_LABEL[role]}
                        checked={perms[role]?.[feat]}
                        onChange={() => toggle(role, feat)}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-end">
          <button
            data-testid="save-settings-button"
            onClick={save}
            disabled={saving}
            className="bg-[#002FA7] text-white px-5 py-2 rounded-md font-semibold hover:bg-[#00247D] transition-all disabled:opacity-50 flex items-center gap-2"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>

        <div className="border-t border-slate-200 pt-8 mt-8">
          <FmcsaCarrierPanel />
        </div>

        <div className="border-t border-slate-200 pt-8 mt-8">
          <ApprovalChainsConfig />
        </div>

        <div className="border-t border-slate-200 pt-8 mt-8">
          <ChatDepartmentsConfig />
        </div>

        <div className="border-t border-slate-200 pt-8 mt-8">
          <CompanyExportCard />
        </div>
      </div>
    </DashboardLayout>
  );
};

const Toggle = ({ testid, label, checked, onChange }) => (
  <label
    data-testid={testid}
    className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-md border cursor-pointer transition-all ${checked ? 'border-[#34C759] bg-green-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
  >
    <span className="text-sm font-medium text-slate-800" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{label}</span>
    <span className={`relative inline-block w-10 h-5 rounded-full transition-colors ${checked ? 'bg-[#34C759]' : 'bg-slate-300'}`}>
      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-5' : 'translate-x-0.5'}`} />
    </span>
    <input type="checkbox" className="sr-only" checked={checked || false} onChange={onChange} />
  </label>
);

export default AdminSettings;
